import type { DateValidationError } from '@mui/x-date-pickers';
import type { MutableRefObject } from 'react';

import dayjs from 'dayjs';

// "Linked" refers to the directional coupling between the two date fields:
// picking a new start date auto-fills the end date as `start + N months - 1 day`,
// unless the user has manually edited the end date (tracked via the
// `endDateManuallyEdited` ref). Editing the end date directly flips that ref,
// disabling future auto-fills until the user clears the end date again.
// The hook is surface-agnostic — each consumer plugs in its own
// setStartDate / setEndDate so CLD's ISO-string storage and Invite's Date
// storage stay surface-specific.

const DEFAULT_LEASE_DURATION_MONTHS = 12;

type SetDate = (
  date: dayjs.Dayjs | null,
  options?: {
    shouldValidate?: boolean;
    shouldDirty?: boolean;
    shouldTouch?: boolean;
  },
) => void;

export type UseLinkedLeaseDateRangeOptions = {
  setStartDate: SetDate;
  setEndDate: SetDate;
  triggerStartDate: () => void;
  triggerEndDate: () => void;
  endDateManuallyEdited: MutableRefObject<boolean>;
  leaseDurationMonths?: number;
  onStartDateChanged?: (value: dayjs.Dayjs | null) => void;
  onEndDateChanged?: (value: dayjs.Dayjs | null) => void;
};

export type UseLinkedLeaseDateRangeResult = {
  onStartDateChange: (value: dayjs.Dayjs | null) => void;
  onEndDateChange: (value: dayjs.Dayjs | null) => void;
  onStartDateError: (error: DateValidationError, value: dayjs.Dayjs | null) => void;
  onEndDateError: (error: DateValidationError, value: dayjs.Dayjs | null) => void;
};

export const useLinkedLeaseDateRange = ({
  setStartDate,
  setEndDate,
  triggerStartDate,
  triggerEndDate,
  endDateManuallyEdited,
  leaseDurationMonths = DEFAULT_LEASE_DURATION_MONTHS,
  onStartDateChanged,
  onEndDateChanged,
}: UseLinkedLeaseDateRangeOptions): UseLinkedLeaseDateRangeResult => {
  const onStartDateChange = (newValue: dayjs.Dayjs | null) => {
    if (newValue !== null && !newValue.isValid()) {
      return;
    }

    onStartDateChanged?.(newValue);

    setStartDate(newValue, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });

    if (newValue && !endDateManuallyEdited.current) {
      const derivedEndDate = newValue.add(leaseDurationMonths, 'month').subtract(1, 'day');
      setEndDate(derivedEndDate, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }

    triggerEndDate();
    triggerStartDate();
  };

  const onEndDateChange = (newValue: dayjs.Dayjs | null) => {
    if (newValue !== null && !newValue.isValid()) {
      return;
    }

    onEndDateChanged?.(newValue);

    endDateManuallyEdited.current = newValue !== null;

    setEndDate(newValue, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });

    triggerEndDate();
    triggerStartDate();
  };

  const onStartDateError = (_error: DateValidationError, value: dayjs.Dayjs | null) => {
    if (value?.isValid()) {
      setStartDate(value);
    }
  };

  const onEndDateError = (_error: DateValidationError, value: dayjs.Dayjs | null) => {
    if (value?.isValid()) {
      setEndDate(value);
    }
  };

  return {
    onStartDateChange,
    onEndDateChange,
    onStartDateError,
    onEndDateError,
  };
};
